import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {logoRES} from "./logoBase";

/**
 * Dodaje logo w nagłówku każdej strony
 */
const addLogoToPage = (pdf, logoImage, margin = 10) => {
    const logoWidth = 70;
    const logoHeight = 15;
    pdf.addImage(logoImage, 'PNG', margin, margin, logoWidth, logoHeight);
};

/**
 * Dodaje napis na określonej pozycji Y
 */
const addOrderNameToPage = (pdf, officialOrderName, margin = 10, textY) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(`Official order name: ${officialOrderName}`, margin, textY);
};

/**
 * Odczytuje elementy legendy z wykresu - usuwa duplikaty
 */
const extractLegendItems = (chartElement) => {
    const legendItems = [];
    const seenTexts = new Set(); // Śledzenie unikalnych tekstów

    // Szukaj wszystkich div'ów które zawierają legendę (struktura: color box + text)
    const legendContainers = chartElement.querySelectorAll('div[style*="display: flex"]');

    legendContainers.forEach(container => {
        // Szukaj wewnątrz color box'a (mały kwadrat z kolorem)
        const colorBox = container.querySelector('div[style*="background-color"]');
        const textSpan = container.querySelector('span');

        if (colorBox && textSpan) {
            const text = textSpan.textContent.trim();
            if (text.length > 0 && !seenTexts.has(text)) { // Sprawdź czy tekst już istnieje
                // Ekstrahuj kolor z inline style
                const styleAttr = colorBox.getAttribute('style');
                const colorMatch = styleAttr.match(/background-color:\s*([^;]+)/);
                const color = colorMatch ? colorMatch[1].trim() : 'rgb(0, 0, 0)';

                legendItems.push({
                    color,
                    label: text
                });
                seenTexts.add(text); // Dodaj tekst do Set'u
            }
        }
    });

    return legendItems;
};

/**
 * Dodaje legendę do PDF na nowej stronie - renderuje HTML i przechwytuje jako obraz
 */
const addLegendPageToPDF = async (pdf, legendItems, margin = 10) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const logoHeight = 15;
    const logoTopMargin = margin + logoHeight + 2;

    console.log(`%c🎨 Tworzenie legendy jako HTML...`, 'color: blue; font-weight: bold;');

    // ✅ UTWÓRZ HTML legendy
    const legendHTML = document.createElement('div');
    legendHTML.style.cssText = `
        position: fixed;
        left: -9999px;
        top: -9999px;
        width: 800px;
        padding: 20px;
        backgroundColor: white;
        fontFamily: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif;
        fontSize: 13px;
        color: rgb(52, 71, 103);
    `;

    // Tytuł
    const titleDiv = document.createElement('h2');
    titleDiv.style.cssText = `
        margin: 0 0 16px 0;
        fontSize: 14px;
        fontWeight: bold;
        color: rgb(52, 71, 103);
    `;
    titleDiv.textContent = 'Legenda';
    legendHTML.appendChild(titleDiv);

    // Elementy legendy
    legendItems.forEach((item) => {
        const itemDiv = document.createElement('div');
        itemDiv.style.cssText = `
            display: flex;
            alignItems: center;
            gap: 10px;
            marginBottom: 8px;
            fontSize: 13px;
        `;

        // Kolorowy kwadrat
        const colorBox = document.createElement('div');
        colorBox.style.cssText = `
            width: 12px;
            height: 12px;
            backgroundColor: ${item.color};
            borderRadius: 2px;
            flexShrink: 0;
        `;

        // Tekst
        const textSpan = document.createElement('span');
        textSpan.style.cssText = `
            color: rgb(52, 71, 103);
            fontWeight: 500;
        `;
        textSpan.textContent = item.label;

        itemDiv.appendChild(colorBox);
        itemDiv.appendChild(textSpan);
        legendHTML.appendChild(itemDiv);
    });

    // Dodaj do DOM tymczasowo
    document.body.appendChild(legendHTML);

    // Czekaj na renderowanie
    await new Promise(resolve => setTimeout(resolve, 500));

    // ✅ PRZECHWYCENIE HTML'a jako canvas
    const canvasLegend = await html2canvas(legendHTML, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        timeout: 15000
    });


    // Usuń z DOM
    document.body.removeChild(legendHTML);

    // ✅ DODAJ DO PDF
    pdf.addPage();
    addLogoToPage(pdf, logoRES, margin);

    const imgDataLegend = canvasLegend.toDataURL('image/jpeg', 0.75);

    // Skalowanie dla PDF
    const legendMargin = margin;
    let legendWidth = pageWidth - (legendMargin * 2);
    let legendHeight = (canvasLegend.height * legendWidth) / canvasLegend.width;

    const maxHeight = pageHeight - logoTopMargin - margin;
    if (legendHeight > maxHeight) {
        legendHeight = maxHeight;
        legendWidth = (canvasLegend.width * legendHeight) / canvasLegend.height;
    }

    const xPosition = legendMargin;
    const yPosition = logoTopMargin;

    pdf.addImage(
        imgDataLegend,
        'JPEG',
        xPosition,
        yPosition,
        legendWidth,
        legendHeight
    );

};

/**
 * Konwertuje RGB string na HEX
 */
const rgbToHex = (rgbString) => {
    const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
        const r = parseInt(match[1]).toString(16).padStart(2, '0');
        const g = parseInt(match[2]).toString(16).padStart(2, '0');
        const b = parseInt(match[3]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    }
    return '#000000';
};

/**
 * Konwertuje HEX na RGB array dla jsPDF
 */
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : [0, 0, 0];
};

// Funkcja do pobierania pojedynczego wykresu - ASYNC!
export const downloadChartPDF = async (chartId, chartTitle = 'chart', officialOrderName = '', isFullscreen = false) => {
    try {
        let chartElement;

        if (isFullscreen) {
            // Szukaj wykresu w fullscreen containerze
            const fullscreenContainer = document.querySelector('div[data-fullscreen-content]');
            if (fullscreenContainer) {
                chartElement = fullscreenContainer.querySelector(`#${chartId}`);
            }
        } else {
            // Normalny selektor
            chartElement = document.getElementById(chartId);
        }

        if (!chartElement) {
            return;
        }


        // Ukryj span'y z "Download"
        const spans = chartElement.querySelectorAll('span');
        const hiddenSpans = [];

        spans.forEach(span => {
            if (span.textContent.toLowerCase().includes('download')) {
                let parent = span.parentElement;
                if (parent) {
                    const originalDisplay = parent.style.display;
                    parent.style.display = 'none';
                    hiddenSpans.push({ element: parent, originalDisplay });
                }
            }
        });

        // STRONA 1: Przechwycenie wykresu bez żadnych zmian stylów
        const canvasChart = await html2canvas(chartElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            timeout: 15000
        });

        const dataSize = (canvasChart.toDataURL('image/jpeg', 0.75).length / 1024).toFixed(2);

        // Przywróć oryginalne style na stronie (ukryty Download)
        hiddenSpans.forEach(({ element, originalDisplay }) => {
            element.style.display = originalDisplay;
        });

        const imgDataChart = canvasChart.toDataURL('image/jpeg', 0.75);

        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
            compress: true
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const bottomMargin = 10;
        const logoHeight = 15;
        const textHeight = 6;
        const logoTopMargin = margin + logoHeight + 2;
        const availableHeightPerPage = pageHeight - logoTopMargin - bottomMargin;

        // --- STRONA 1: WYKRES ---
        addLogoToPage(pdf, logoRES, margin);

        // ✅ PROPORCJONALNE SKALOWANIE DLA WSZYSTKICH PRZYPADKÓW
        const chartMargin = 5;
        let imgWidth = pageWidth - (chartMargin * 2);
        let imgHeight = (canvasChart.height * imgWidth) / canvasChart.width;

        // Jeśli za wysoki - zmniejsz proportionally
        const maxHeight = availableHeightPerPage - 15;
        if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = (canvasChart.width * imgHeight) / canvasChart.height;
        }


        // Dodaj wykres na pierwszej stronie
        let xPosition = chartMargin;
        if (!isFullscreen) {
            xPosition = (pageWidth - imgWidth) / 2;
        }
        let currentChartY = logoTopMargin;

        if (officialOrderName) {
            const orderNameY = logoTopMargin + 3;
            addOrderNameToPage(pdf, officialOrderName, margin, orderNameY);
            currentChartY = orderNameY + textHeight + 2;
        }

        pdf.addImage(
            imgDataChart,
            'JPEG',
            xPosition,
            currentChartY,
            imgWidth,
            imgHeight
        );

        // Odczytaj legendę z wykresu i dodaj na nową stronę
        const legendItems = extractLegendItems(chartElement);

        legendItems.forEach((item, index) => {
            const hasPolishChars = /[ąćęłńóśźż]/i.test(item.label);
            const polishIndicator = hasPolishChars ? '🇵🇱' : '';
        });

        if (legendItems.length > 0) {
            // ✅ ASYNC - czekaj na dodanie legendy
            await addLegendPageToPDF(pdf, legendItems, margin);
        }

        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${chartTitle}-${timestamp}.pdf`;

        pdf.save(filename);

    } catch (e) {}
};